import {
  useRef, useEffect, useState, type ReactNode, type RefObject,
  ChangeEvent,
} from 'react';
import {
  ReportGrid,
  settingsInitialExpansion,
  type ReportGridDensity,
  type ReportGridDataset,
  type ReportGridCellContext,
  type ReportGridSettings,
} from '../../../../components/ReportGrid';
import Button from '../../../core/Button';
import Popover from '../../../core/Popover';
import List from '../../../core/List';
import ListItem from '../../../core/ListItem';
import Tooltip from '../../../core/Tooltip';
import IconButton from '../../../core/IconButton';
import TextArea from '../../../core/TextArea';
import useDeviceType from '../../../hooks/useDeviceType';
import {
  ChartTable,
  PNG,
  Trash,
  Clone,
  Export,
  MoreVert,
  Warning,
  AddComment,
  CommentExclamation,
} from '../../../icons';
import { EmptyReportCard, ReportGridKpiCard, Comment } from './styled/GridKpisCard.styled';

interface Definition {
  error: boolean;
  settings: ReportGridSettings;
  dataset: ReportGridDataset;
}

interface Item {
  id: string;
  name: string;
  comment?: string;
}

const defaultCardSizes = {
  h: 7, w: 4, x: 0, y: 0,
};

interface DependencyFile {
  allowed?: boolean;
}

interface ReportGridCardProps {
  item: Item;
  definition: Definition;
  canEditReport: boolean;
  collection: string;
  cardSizes: Record<string, number>;
  onReportRetry: (itemId: string) => void;
  reportGridSettings: (item: Item) => void;
  onReportDensityChange: (itemId: string, density: ReportGridDensity) => void;
  reportGridDetails: (item: Item, cell: ReportGridCellContext) => void;
  editPainel: boolean;
  cardSelection: (item: Item) => void;
  updateReportHeight: (itemId: string, height: number) => void;
  downloadImgCard: (ref: HTMLDivElement | null, itemName: string) => void;
  deleteCard: (item: Item) => void;
  cloneCard: (item: Item) => void;
  transferKpi: (item: Item) => void;
  dependencyFile: DependencyFile;
  getKpiWithoutDependencyMessage: (dependencyFile: DependencyFile, item: Item) => void;
  createKpiComment: (comment: string, itemId: string) => void;
}

interface AutoHeightReportProps {
  contentRef: RefObject<HTMLDivElement | null>;
  children: ReactNode;
  onHeightChange: (height: number) => void;
  handleCardSelection: () => void;
  editPainel: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const emptyFunc = () => {};
const emptyObj = {};

const deviceThreshold = (device: string) => {
  switch (device) {
    case 'desktop':
      return 50;
    case 'tablet':
      return 12;
    case 'phone':
      return 4;
    default:
      return 0;
  }
};

const AutoHeightReport = ({
  contentRef,
  children,
  onHeightChange,
  handleCardSelection,
  editPainel,
  onMouseEnter,
  onMouseLeave,
}: AutoHeightReportProps) => {
  useEffect(() => {
    const element = contentRef.current;
    if (!element) return undefined;

    let animationFrame: number;

    const measure = () => {
      cancelAnimationFrame(animationFrame);

      animationFrame = requestAnimationFrame(() => {
        // Não usar a altura da tela inteira como altura do card.
        if (document.fullscreenElement) return;

        onHeightChange(element.getBoundingClientRect().height);
      });
    };

    const observer = new ResizeObserver(measure);
    observer.observe(element);

    document.addEventListener('fullscreenchange', measure);
    measure();

    return () => {
      observer.disconnect();
      cancelAnimationFrame(animationFrame);
      document.removeEventListener('fullscreenchange', measure);
    };
  }, [onHeightChange]);

  return (
    <ReportGridKpiCard
      ref={contentRef}
      onClick={handleCardSelection}
      className={editPainel ? 'disableCardEvents' : ''}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </ReportGridKpiCard>
  );
};

const ReportGridCard = ({
  item,
  definition,
  canEditReport,
  collection,
  cardSizes = defaultCardSizes,
  onReportRetry,
  reportGridSettings,
  onReportDensityChange,
  reportGridDetails,
  editPainel,
  cardSelection = emptyFunc,
  updateReportHeight = emptyFunc,
  downloadImgCard = emptyFunc,
  deleteCard = emptyFunc,
  cloneCard = emptyFunc,
  transferKpi = emptyFunc,
  dependencyFile = emptyObj,
  getKpiWithoutDependencyMessage = emptyFunc,
  createKpiComment = emptyFunc,
}: ReportGridCardProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [toggleMenuCard, setToggleMenuCard] = useState<boolean>(false);
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);
  const [showCommentContainer, setShowCommentContainer] = useState<boolean>(false);
  const [isEditComment, setIsEditComment] = useState<boolean>(false);
  const [comment, setComment] = useState<string>(item.comment || '');
  const device = useDeviceType();

  const closeMenu = () => setMenuIsOpen(false);
  const toggleMenu = () => setMenuIsOpen((p) => !p);

  const handleOnReportRetry = () => onReportRetry(item.id);
  const handleReportGridSettings = () => reportGridSettings(item);
  const handleOnReportDensityChange = (density: ReportGridDensity) => {
    onReportDensityChange(item.id, density);
  };
  const handleReportGridDetails = (cell: ReportGridCellContext) => {
    reportGridDetails?.(item, cell);
  };
  const handleCardSelection = () => cardSelection(item);
  const handleOnHeightChange = (height: number) => {
    updateReportHeight(item.id, height);
  };

  const handleOpenMenuCard = () => {
    setToggleMenuCard(true);
  };

  useEffect(() => {
    setToggleMenuCard(menuIsOpen);
  }, [menuIsOpen]);

  const handleHoverCloseMenuCard = () => {
    setToggleMenuCard(menuIsOpen);
  };

  const handleCardImgDonwload = () => downloadImgCard(contentRef.current, item.name);

  const handleDelete = () => deleteCard(item);

  const handleClone = () => cloneCard(item);

  const handleTransferKpi = () => transferKpi(item);

  const closeCommentContainer = (resetComment: boolean = false) => {
    setShowCommentContainer(false);
    setIsEditComment(false);
    if (resetComment) setComment(item.comment ?? '');
  };

  const toggleCommentContainer = () => setShowCommentContainer((p) => !p);
  const showCommentEditor = () => {
    if (canEditReport) setIsEditComment(true);
  };
  const showAddComment = () => {
    setShowCommentContainer((p) => !p);
    setIsEditComment(true);
  };

  const onSubmitComment = async () => {
    setShowCommentContainer(false);
    setIsEditComment(false);
    await createKpiComment(comment, item.id);
  };

  const checkComment = () => !!item?.comment?.trim();

  return (
    <AutoHeightReport
      contentRef={contentRef}
      handleCardSelection={handleCardSelection}
      editPainel={editPainel}
      onHeightChange={handleOnHeightChange}
      onMouseEnter={handleOpenMenuCard}
      onMouseLeave={handleHoverCloseMenuCard}
    >
      <Popover
        style={{
          borderRadius: '8px',
          backgroundColor: 'trasparent',
          overflow: 'hidden',
          minWidth: 'auto',
        }}
        offset={[0, 0]}
        open={!editPainel && (device !== 'desktop' || toggleMenuCard)}
        stayOpened
        // closePopover={() => {}}
        direction="right-start"
        customPopperModifier={[
          {
            name: 'preventOverflow',
            options: {
              altBoundary: true,
              altAxis: (cardSizes.x + cardSizes.w) >= deviceThreshold(device),
            },
          },
        ]}
      >
        <Popover.Action>
          {definition ? (
            <>
              {!definition.dataset ? (
                <EmptyReportCard>
                  <h3>{definition.error ? 'Não foi possível carregar o explorador' : 'Carregando explorador…'}</h3>
                  <p role={definition.error ? 'alert' : 'status'}>
                    {definition.error || 'Consultando os dados da fonte.'}
                  </p>
                  {definition.error && (
                    <Button type="button" onClick={handleOnReportRetry}>
                      Tentar novamente
                    </Button>
                  )}
                  {canEditReport && (
                    <Button type="button" variant="outlined" onClick={handleReportGridSettings}>
                      Configurar explorador
                    </Button>
                  )}
                </EmptyReportCard>
              ) : (
                <ReportGrid
                  dataset={definition.dataset}
                  mode={canEditReport ? 'edit' : 'view'}
                  canEdit={canEditReport}
                  height={definition.settings.height}
                  search={definition.settings.search}
                  density={definition.settings.density}
                  onDensityChange={handleOnReportDensityChange}
                  appearance={definition.settings.appearance}
                  title={{
                    ...definition.settings.title,
                    text: definition.settings.title.text || undefined,
                  }}
                  initialExpansion={settingsInitialExpansion(
                    definition.settings.initial,
                  )}
                  showRowTotals={definition.settings.showRowTotals}
                  showColumnTotals={definition.settings.showColumnTotals}
                  showBalance={definition.settings.showBalance}
                  onRequestSettings={handleReportGridSettings}
                  onCellDoubleClick={handleReportGridDetails}
                />
              )}
            </>
          ) : (
            <EmptyReportCard>
              <span className="empty-report-icon" aria-hidden="true">
                <ChartTable />
              </span>
              <h3>Explorador não configurado</h3>
              <p>
                {canEditReport
                  ? 'Selecione uma fonte de dados e organize as linhas e colunas para começar.'
                  : 'O responsável pelo painel ainda não configurou este explorador.'}
              </p>
              {canEditReport && (
                <Button
                  type="button"
                  color="primary"
                  size="medium"
                  onClick={handleReportGridSettings}
                >
                  Configurar explorador
                </Button>
              )}
            </EmptyReportCard>
          )}
        </Popover.Action>
        {/* @ts-expect-error not typed yet */}
        <Popover.Content>
          {!editPainel && !!definition && !!definition.dataset && (
            <>
              {(checkComment() || canEditReport) && device !== 'phone' && (
                <Popover
                  open={showCommentContainer}
                  closePopover={closeCommentContainer}
                  stayOpened
                  offset={[-20, 2]}
                  style={{
                    width: '250px', padding: '1rem 1rem', whiteSpace: 'normal', zIndex: '1050',
                  }}
                >
                  <Popover.Action>
                    {checkComment() ? (
                      <Tooltip text="Ver comentário" direction="left">
                        <IconButton
                          color="secondary"
                          shape="circle"
                          variant="pattern"
                          size="medium"
                          style={{ boxShadow: 'none' }}
                          onClick={toggleCommentContainer}
                        >
                          <CommentExclamation />
                        </IconButton>
                      </Tooltip>
                    ) : canEditReport && (
                      <Tooltip text="Adicionar comentário" direction="left">
                        <IconButton
                          shape="circle"
                          variant="pattern"
                          size={cardSizes.w > 5 ? 'medium' : 'small'}
                          style={{ boxShadow: 'none' }}
                          onClick={showAddComment}
                        >
                          <AddComment />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Popover.Action>
                  {/* @ts-expect-error not typed yet */}
                  <Popover.Content>
                    <span style={{ color: '#696969', fontSize: '0.85rem', fontWeight: 'bold' }}>
                      Comentário
                    </span>
                    {!isEditComment ? (
                      <Comment
                        onClick={showCommentEditor}
                        // @ts-expect-error not typed yet
                        isOwner={canEditReport}
                        story={collection === 'kpis'}
                      >
                        {item.comment}
                      </Comment>
                    ) : (
                      <div>
                        <TextArea
                          // @ts-expect-error not typed yet
                          value={comment}
                          label=""
                          onChange={
                            (e: ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)
                          }
                          style={{ height: '100px' }}
                        />
                        <div style={{
                          display: 'flex', justifyContent: 'center', gap: '5px', paddingTop: '5px',
                        }}
                        >
                          <Button
                            color="primary"
                            variant="outlined"
                            size="medium"
                            shape="rounded"
                            onClick={() => closeCommentContainer(true)}
                          >
                            Cancelar
                          </Button>
                          <Button
                            color="primary"
                            variant="contained"
                            size="medium"
                            shape="rounded"
                            onClick={onSubmitComment}
                          >
                            Salvar
                          </Button>
                        </div>
                      </div>
                    )}
                  </Popover.Content>
                </Popover>
              )}
              {(!dependencyFile?.allowed && device !== 'phone') && (
                <Tooltip text="Sem acesso ao arquivo de origem" direction="left">
                  <IconButton
                    color="error"
                    shape="circle"
                    variant="pattern"
                    size={cardSizes.w > 5 ? 'medium' : 'small'}
                    className="kpi-options"
                    style={{ boxShadow: 'none' }}
                    onClick={() => getKpiWithoutDependencyMessage(dependencyFile, item)}
                  >
                    <Warning aria-hidden="true" data-dismiss="modal" aria-label="Close" />
                  </IconButton>
                </Tooltip>
              )}
              <Popover
                open={menuIsOpen}
                closePopover={closeMenu}
                offset={[-20, 2]}
                style={{ zIndex: '1050' }}
              >
                <Popover.Action>
                  <Tooltip text="Mais opções" direction="left">
                    <IconButton
                      shape="circle"
                      variant="pattern"
                      size={cardSizes.w > 5 ? 'medium' : 'small'}
                      className="kpi-options"
                      style={{ boxShadow: 'none', transform: 'rotate(90deg)' }}
                      onClick={toggleMenu}
                    >
                      <MoreVert
                        aria-hidden="true"
                        // type="button"
                        data-dismiss="modal"
                        aria-label="Close"
                      />
                    </IconButton>
                  </Tooltip>
                </Popover.Action>
                {/* @ts-expect-error not typed yet */}
                <Popover.Content>
                  <List>
                    <ListItem
                      disabled={false}
                      noOutline
                      onClick={handleCardImgDonwload}
                    >
                      <PNG />
                      Salvar PNG
                    </ListItem>
                    {canEditReport && device !== 'phone' && (
                      <>
                        <ListItem disabled={false} noOutline onClick={handleDelete}>
                          <Trash />
                          Apagar
                        </ListItem>
                        <ListItem disabled={false} noOutline onClick={handleClone}>
                          <Clone />
                          Clonar
                        </ListItem>
                        <ListItem disabled={false} noOutline onClick={handleTransferKpi}>
                          <Export />
                          Transferir KPI
                        </ListItem>
                      </>
                    )}
                  </List>
                </Popover.Content>
              </Popover>
            </>
          )}
          {!editPainel && !(definition && definition.dataset) && canEditReport && device !== 'phone' && (
            <ListItem disabled={false} noOutline onClick={handleDelete}>
              <Trash />
              Apagar
            </ListItem>
          )}
        </Popover.Content>
      </Popover>
    </AutoHeightReport>
  );
};

export default ReportGridCard;
